/**
 * Phase 2 Sub-Slice 2E — Manual GBP verification modal.
 *
 * Visitor pastes either a Google Maps / GBP URL OR a Place ID. The value
 * is POSTed to /api/public/resolve-identity for safe server-side parsing
 * (client never fetches the URL). On success, the resolved candidate is
 * returned to the parent which then re-runs the assessment with
 * confirmed_place_id + correction_method="manual_url" | "manual_place_id".
 */
import React, { useState, useEffect, useRef } from "react";
import { LinkSimple, X, ShieldCheck } from "@phosphor-icons/react";

export default function ManualVerificationModal({ onResolve, onCancel, loading = false }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting && !loading) onCancel && onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, loading, onCancel]);

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (submitting || loading) return;
    const raw = (input || "").trim();
    if (!raw) {
      setError("Paste your Google Maps listing URL or a Place ID.");
      return;
    }
    if (raw.length > 2048) {
      setError("That value is too long. Please paste a Google Maps URL or a Place ID.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await onResolve(raw);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || loading;

  return (
    <div
      className="fixed inset-0 z-[95] bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-verify-title"
      data-testid="manual-verify-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 border-b border-zinc-200 flex items-start justify-between gap-4">
          <div>
            <div className="label-tiny text-[#2D5BFF] mb-1.5">Verify with a Google Listing Link</div>
            <h2
              id="manual-verify-title"
              className="font-display text-xl sm:text-2xl font-black text-zinc-900 leading-tight"
            >
              Point us to your listing.
            </h2>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              Paste your Google Maps listing URL (from google.com/maps) or your
              Google Place ID. We&apos;ll verify the listing on our side — we
              never open the link in your browser.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel && onCancel()}
            disabled={busy}
            aria-label="Close"
            className="shrink-0 text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
            data-testid="manual-verify-close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 flex-1 overflow-y-auto">
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <LinkSimple size={12} weight="bold" aria-hidden="true" />
              Google Maps URL or Place ID
            </span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 2048))}
              disabled={busy}
              placeholder="https://www.google.com/maps/place/..."
              className="mt-2 w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#2D5BFF] focus:border-[#2D5BFF] disabled:bg-zinc-50"
              autoComplete="off"
              spellCheck="false"
              data-testid="manual-verify-input"
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? "manual-verify-error" : undefined}
            />
            {error && (
              <p id="manual-verify-error" className="text-xs text-red-600 mt-2" data-testid="manual-verify-error">
                {error}
              </p>
            )}
          </label>
          <div className="text-xs text-zinc-500 leading-relaxed flex items-start gap-2 bg-zinc-50 border border-zinc-200 rounded-md p-3">
            <ShieldCheck size={16} weight="bold" className="shrink-0 text-emerald-600 mt-0.5" aria-hidden="true" />
            <span>
              Supported: google.com/maps/place URLs and Google Place IDs.
              Shortened links (goo.gl, share.google) and third-party URLs are
              rejected for your security.
            </span>
          </div>
        </form>

        <div className="p-5 sm:p-6 border-t border-zinc-200 flex items-center justify-end gap-3 bg-zinc-50/60">
          <button
            type="button"
            onClick={() => !busy && onCancel && onCancel()}
            disabled={busy}
            className="text-sm px-4 py-2 rounded-md border border-zinc-300 hover:bg-white transition disabled:opacity-50"
            data-testid="manual-verify-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !input.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm text-white hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#2D5BFF" }}
            data-testid="manual-verify-submit"
          >
            {busy ? (<><span className="spinner" /> Verifying…</>) : (<>Verify listing</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
